import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "high_match_alerts", label: "High-match job alerts" },
  { key: "weekly_digest", label: "Weekly digest" },
  { key: "feature_updates", label: "Feature updates" },
  { key: "announcements", label: "Announcements" },
  { key: "tips_and_guides", label: "Tips and guides" },
];

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const all = params.get("all") === "1";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [prefs, setPrefs] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Missing token.");
      setLoading(false);
      return;
    }
    axios.get(`${API}/unsubscribe/prefs?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        setEmail(r.data.email || "");
        setPrefs(r.data.preferences || {});
        if (all) {
          // One-click "unsubscribe from all" flow.
          await axios.post(`${API}/unsubscribe?token=${encodeURIComponent(token)}&all=true`);
          setPrefs(Object.fromEntries(CATEGORIES.map((c) => [c.key, false])));
          toast.success("Unsubscribed from all Sisyphean emails.");
        }
      })
      .catch((e) => setError(e?.response?.data?.detail || "Invalid link."))
      .finally(() => setLoading(false));
  }, [token, all, API]);

  const toggle = (k) => setPrefs((p) => ({ ...p, [k]: !p[k] }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/unsubscribe?token=${encodeURIComponent(token)}`, prefs);
      toast.success("Preferences saved.");
    } catch (e) {
      toast.error("Couldn't save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="unsub-page">
      <header className="border-b border-foreground/20 h-14 md:h-16 flex items-center px-5 md:px-12">
        <Logo size="md" />
      </header>
      <div className="max-w-xl mx-auto px-5 md:px-12 py-10 sm:py-20">
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading your preferences...</div>
        ) : error ? (
          <div className="panel p-6">
            <div className="terminal-label text-primary mb-3">[ INVALID.LINK ]</div>
            <p className="text-sm text-foreground/80">{error}</p>
          </div>
        ) : (
          <>
            <div className="terminal-label text-primary mb-3">[ EMAIL.PREFERENCES ]</div>
            <h1 className="font-display text-3xl uppercase font-bold tracking-tight mb-2">Manage your Sisyphean emails</h1>
            <p className="text-sm text-muted-foreground mb-8">
              We&apos;ll only send what you check. Signed in as <span className="font-mono text-foreground">{email}</span>.
            </p>

            <div className="space-y-2 mb-6">
              {CATEGORIES.map((c) => (
                <label key={c.key} className="flex items-start gap-3 p-3 border border-foreground/15 hover:border-foreground/40 cursor-pointer" data-testid={`unsub-${c.key}`}>
                  <input type="checkbox" checked={!!prefs[c.key]} onChange={() => toggle(c.key)} className="mt-1 w-4 h-4 accent-primary" />
                  <span className="text-sm">{c.label}</span>
                </label>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-between">
              <button
                onClick={() => setPrefs(Object.fromEntries(CATEGORIES.map((c) => [c.key, false])))}
                className="btn-ghost text-[11px]"
                data-testid="unsub-all"
              >Uncheck all</button>
              <button
                onClick={save}
                disabled={saving}
                className="btn-primary text-[11px] inline-flex items-center gap-2"
                data-testid="unsub-save"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                Save preferences
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
